import { useCallback, useEffect, useLayoutEffect, useRef } from "react"

/** Five minutes. */
export const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000

/** Injectable clock and timers. */
export interface AutoRefreshClock {
  /** Epoch milliseconds. */
  now: () => number
  setTimeout: (callback: () => void, delayMs: number) => unknown
  clearTimeout: (handle: unknown) => void
}

/**
 * Looks the globals up at call time, so fake timers installed after this module
 * is imported (vi.useFakeTimers) are honoured.
 */
const realClock: AutoRefreshClock = {
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => globalThis.setTimeout(callback, delayMs),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
}

const isPageVisible = () => document.visibilityState === "visible"

export interface UseAutoRefreshOptions<Selection, Result extends { applied?: unknown; errors: readonly unknown[] }> {
  /** `useTimeRangeController().apply`. */
  apply: (selection: Selection) => Result
  /** `useTimeRangeController().refresh`. Read through a ref, so a changing identity does not reset the timer. */
  refresh: () => void
  /** True while the applied range is Relative. A Custom range is never refreshed automatically. */
  enabled: boolean
  /** Defaults to the real clock. Read once, on the first render. */
  clock?: AutoRefreshClock
}

export interface UseAutoRefreshResult<Selection, Result> {
  /** Pass to `TimeRangeControl`'s `onApply`. A successful Apply restarts the interval. Stable identity. */
  apply: (selection: Selection) => Result
  /** Pass to `TimeRangeControl`'s `onOpenChange`. The schedule is paused while the popup is open. Stable identity. */
  onOpenChange: (open: boolean) => void
}

/**
 * Refreshes a Relative time range every five minutes by calling `refresh`.
 *
 * The schedule is paused while the popup is open, while the page is hidden and while the range is Custom.
 * When it resumes after being due, it refreshes once (a single catch-up), however long it was paused.
 */
export function useAutoRefresh<Selection, Result extends { applied?: unknown; errors: readonly unknown[] }>(
  options: UseAutoRefreshOptions<Selection, Result>
): UseAutoRefreshResult<Selection, Result> {
  const clock = useRef(options.clock ?? realClock).current
  const latest = useRef(options)
  const lastRefreshAt = useRef(0)
  const timer = useRef<unknown>(undefined)
  const popupOpen = useRef(false)
  const disposed = useRef(true)

  // Layout effects run before any passive effect, so the control's close notification sees current values.
  useLayoutEffect(() => {
    latest.current = options
  })

  const evaluate = useCallback(() => {
    if (timer.current !== undefined) {
      clock.clearTimeout(timer.current)
      timer.current = undefined
    }
    const { enabled, refresh } = latest.current
    if (disposed.current || !enabled || popupOpen.current || !isPageVisible()) return

    // If the wall clock has moved backwards (for example a manual change or an NTP correction), re-stamp to the
    // new, earlier time. Clamping elapsed time at 0 alone is not enough: elapsed would stay 0 until the wall clock
    // caught up with the old stamp, delaying the next refresh by the size of the jump.
    if (clock.now() < lastRefreshAt.current) lastRefreshAt.current = clock.now()

    if (Math.max(0, clock.now() - lastRefreshAt.current) >= AUTO_REFRESH_INTERVAL_MS) {
      lastRefreshAt.current = clock.now()
      refresh() // at most once per evaluation, so at most one catch-up
    }
    const elapsed = Math.max(0, clock.now() - lastRefreshAt.current)
    timer.current = clock.setTimeout(evaluate, AUTO_REFRESH_INTERVAL_MS - elapsed)
  }, [clock])

  useEffect(() => {
    disposed.current = false
    lastRefreshAt.current = clock.now()
    document.addEventListener("visibilitychange", evaluate)
    evaluate()
    return () => {
      // Set before the children's cleanups run: the control reports "closed" on unmount and must not re-arm.
      disposed.current = true
      document.removeEventListener("visibilitychange", evaluate)
      evaluate() // clears the timer, then returns because disposed
    }
  }, [clock, evaluate])

  const { enabled } = options
  useEffect(() => {
    evaluate()
  }, [enabled, evaluate])

  const onOpenChange = useCallback(
    (open: boolean) => {
      popupOpen.current = open
      evaluate()
    },
    [evaluate]
  )

  const apply = useCallback(
    (selection: Selection) => {
      const result = latest.current.apply(selection)
      // The same condition the controller uses to commit.
      if (result.applied !== undefined && result.errors.length === 0) lastRefreshAt.current = clock.now()
      return result
    },
    [clock]
  )

  return { apply, onOpenChange }
}
